/**
 * Prevents dark mode by removing the "dark" class from all elements
 * and setting up a MutationObserver to prevent it from being added
 */
export function preventDarkMode(): () => void {
  // Function to remove dark class from all elements
  const removeDarkClass = () => {
    // Remove from document element
    if (document.documentElement.classList.contains('dark')) {
      document.documentElement.classList.remove('dark');
    }

    // Remove from body
    if (document.body?.classList.contains('dark')) {
      document.body.classList.remove('dark');
    }

    // Remove from all elements with dark class
    const darkElements = document.querySelectorAll('.dark');
    darkElements.forEach((element) => {
      element.classList.remove('dark');
    });
  };

  // Initial cleanup
  removeDarkClass();

  // Create a MutationObserver to watch for dark class being added
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        const target = mutation.target as HTMLElement;
        if (target.classList.contains('dark')) {
          target.classList.remove('dark');
        }
      }
    });
  });

  // Observe the document element for class changes
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
    subtree: true,
  });

  // Also observe the body when it's available
  if (document.body) {
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });
  }

  // Return cleanup function to stop observing
  return () => {
    observer.disconnect();
  };
}

// Initialize immediately if this file is imported
if (typeof window !== 'undefined') {
  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', preventDarkMode);
  } else {
    preventDarkMode();
  }
}
